<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <h3>
            <i class="fas fa-code-branch"></i>
            Selecionar Branch - {{ appName }}
          </h3>
          <button class="btn-close" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-search">
          <i class="fas fa-search"></i>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            placeholder="Buscar branch..."
            @keydown.esc="close"
          />
          <span v-if="filteredBranches.length" class="result-count">
            {{ filteredBranches.length }} {{ filteredBranches.length === 1 ? 'resultado' : 'resultados' }}
          </span>
        </div>

        <div v-if="loading" class="modal-loading">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Carregando branches...</p>
        </div>

        <div v-else-if="error" class="modal-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>

        <div v-else-if="filteredBranches.length === 0" class="modal-empty">
          <i class="fas fa-search"></i>
          <p v-if="searchQuery">Nenhuma branch encontrada para "{{ searchQuery }}"</p>
          <p v-else>Nenhuma branch disponível</p>
        </div>

        <div v-else class="branches-list">
          <div
            v-for="branch in filteredBranches"
            :key="branch.name"
            class="branch-item"
            @click="selectBranch(branch)"
          >
            <div class="branch-info">
              <div class="branch-header">
                <i class="fas fa-code-branch"></i>
                <span class="branch-name">{{ branch.name }}</span>
              </div>
              <div class="branch-meta">
                <span class="branch-url">
                  <i class="fas fa-link"></i>
                  {{ branch.url }}
                </span>
                <span v-if="branch.lastDeployTime" class="branch-deploy">
                  <i class="fas fa-clock"></i>
                  {{ formatDate(branch.lastDeployTime) }}
                </span>
              </div>
            </div>
            <div class="branch-actions">
              <i 
                class="fas fa-qrcode branch-qrcode-icon" 
                @click.stop="mostrarQRCode(branch)"
                title="Ver QR Code"
              ></i>
              <i class="fas fa-arrow-right"></i>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="close">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BranchSelectorModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    appName: {
      type: String,
      required: true
    },
    cliente: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      searchQuery: ''
    }
  },
  
  computed: {
    ...mapGetters({
      getAppByName: 'amplifyBranches/getAppByName',
      loading: 'amplifyBranches/isLoading',
      error: 'amplifyBranches/getError'
    }),
    
    app() {
      return this.getAppByName(this.appName)
    },
    
    branches() {
      // Filtrar branches principais (develop, master, homolog, production)
      const branchesExcluidas = ['develop', 'master', 'homolog', 'production']
      const allBranches = this.app?.branches || []
      
      return allBranches.filter(branch => 
        !branchesExcluidas.includes(branch.name.toLowerCase())
      )
    },
    
    filteredBranches() {
      if (!this.searchQuery) {
        return this.branches
      }
      
      const query = this.searchQuery.toLowerCase()
      return this.branches.filter(branch => 
        branch.name.toLowerCase().includes(query) ||
        branch.url.toLowerCase().includes(query) ||
        branch.status.toLowerCase().includes(query)
      )
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      } else {
        this.searchQuery = ''
      }
    }
  },
  
  methods: {
    close() {
      this.$emit('close')
    },
    
    async selectBranch(branch) {
      this.$emit('select', branch)
      this.close()
    },
    
    mostrarQRCode(branch) {
      this.$emit('mostrar-qrcode-branch', branch)
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      
      if (diffMins < 1) return 'Agora'
      if (diffMins < 60) return `${diffMins}min atrás`
      
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours}h atrás`
      
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 30) return `${diffDays}d atrás`
      
      const diffMonths = Math.floor(diffDays / 30)
      return `${diffMonths}m atrás`
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--bootstrap-hairline);
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--badge-bootstrap-warning-fg);
    display: flex;
    align-items: center;
    gap: 10px;
    
    i {
      color: var(--lp-chrome-primary-blue);
    }
  }
  
  .btn-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    color: var(--lp-switch-track-off);
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: var(--bootstrap-subtle-bg);
      color: var(--badge-bootstrap-warning-fg);
    }
  }
}

.modal-search {
  position: relative;
  padding: 16px 24px;
  border-bottom: 1px solid var(--bootstrap-hairline);
  
  i.fa-search {
    position: absolute;
    left: 40px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--lp-switch-track-off);
  }
  
  input {
    width: 100%;
    padding: 10px 40px 10px 36px;
    border: 1px solid var(--bootstrap-border);
    border-radius: 8px;
    font-size: 0.938rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--lp-chrome-primary-blue);
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
    
    &::placeholder {
      color: var(--bootstrap-adb5bd);
    }
  }
  
  .result-count {
    position: absolute;
    right: 40px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.813rem;
    color: var(--lp-switch-track-off);
  }
}

.modal-loading,
.modal-error,
.modal-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: var(--lp-switch-track-off);
  
  i {
    font-size: 3rem;
    margin-bottom: 16px;
  }
  
  p {
    margin: 0;
    font-size: 1rem;
  }
}

.modal-error {
  color: var(--lp-chrome-danger);
  
  i {
    color: var(--lp-chrome-danger);
  }
}

.branches-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--bootstrap-subtle-bg);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--bootstrap-border);
    border-radius: 4px;
    
    &:hover {
      background: var(--bootstrap-input-border);
    }
  }
}

.branch-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  margin-bottom: 8px;
  border-radius: 8px;
  border: 1px solid var(--bootstrap-hairline);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bootstrap-subtle-bg);
    border-color: var(--lp-chrome-primary-blue);
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .branch-info {
    flex: 1;
  }
  
  .branch-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    
    i.fa-code-branch {
      color: var(--lp-switch-track-off);
    }
    
    .branch-name {
      font-weight: 600;
      color: var(--badge-bootstrap-warning-fg);
      font-size: 0.938rem;
    }
  }
  
  .branch-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.813rem;
    color: var(--lp-switch-track-off);
    
    span {
      display: flex;
      align-items: center;
      gap: 6px;
      
      i {
        font-size: 0.75rem;
      }
    }
    
    .branch-url {
      color: var(--lp-chrome-primary-blue);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 500px;
    }
  }
  
  .branch-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: 16px;
  }
  
  .branch-qrcode-icon {
    color: var(--lp-chrome-primary-blue);
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 4px;
    background: rgba(0, 123, 255, 0.1);
    transition: all 0.2s ease;
    opacity: 0.8;
    font-size: 0.9em;
    
    &:hover {
      opacity: 1;
      background: rgba(0, 123, 255, 0.2);
      color: var(--lp-chrome-primary-blue-dark);
      transform: scale(1.1);
    }
  }
  
  > .branch-actions > i.fa-arrow-right {
    color: var(--lp-switch-track-off);
    transition: transform 0.2s ease;
  }
  
  &:hover > .branch-actions > i.fa-arrow-right {
    transform: translateX(4px);
  }
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--bootstrap-hairline);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  button {
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 0.938rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    
    &.btn-secondary {
      background: var(--lp-switch-track-off);
      color: white;
      
      &:hover {
        background: var(--bootstrap-secondary-hover);
      }
    }
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.2s ease;
}

.modal-fade-enter .modal-container,
.modal-fade-leave-to .modal-container {
  transform: scale(0.95);
}
</style>

